import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
    DndContext,
    DragOverlay,
    MouseSensor,
    TouchSensor,
    closestCenter,
    pointerWithin,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { isMobile } from 'lib/device'
import useStore from 'state/knovStore'
import { PanelContextProvider } from 'refactor/hooks/usePanelContext'
import {
    getCenterIndex,
    getSwiperRef,
    slideTo as slideToPanel,
    SWIPE_SPEED_SLOW,
} from 'state/imperativeApis/swiperApi'

const AnswersDndRegistryContext = createContext(null)
const GLOBAL_DRAGGING_EVENT = 'knov:global-dragging'
const TITLE_SLOT_PREFIX = 'title-slot:'

// Title swap preview tuning (overlap-only):
// thresholds are in px of vertical intersection between the drag overlay and the title-slot rect
const TITLE_ZONE = {
    enterOverlapPx: 10,
    stayOverlapPx: 6,
}

export function useAnswersDndRegistry() {
    const ctx = useContext(AnswersDndRegistryContext)
    if (!ctx) throw new Error('useAnswersDndRegistry must be used within GlobalAnswersDndProvider')
    return ctx
}

export default function GlobalAnswersDndProvider({ children }) {
    const registryRef = useRef(new Map())
    const lastOverRef = useRef({ overId: null, containerId: null })
    const debugTitleSwapRef = useRef({ lastSig: null, lastAt: 0 })
    const debugIndexRef = useRef({ lastSig: null, lastAt: 0 })
    const pointerPosRef = useRef({ x: null, y: null })
    const titleHoverPointerRef = useRef({ x: null, y: null })
    const sortedToTitleRectRef = useRef({ containerId: null, rect: null })
    // For title drags, dnd-kit `over` on drop can be noisy (container/titleSlot/activeId) after live previews.
    // Track the last preview swap target in terms of the *drag-start* base index, so we can persist correctly.
    const titleDragCommitRef = useRef({ containerId: null, targetBaseIndex: null })
    const sortedToTitleSwapRef = useRef({
        active: false,
        containerId: null,
        baseIds: null, // snapshot taken when entering swap mode
        enteredPointer: { x: null, y: null },
        enteredActiveTop: null,
    })

    const getTitleDragDropIndex = ({
        toContainerId,
        overType,
        rawOverId,
        titleItemId,
        activeId,
    }) => {
        // Title drags: prefer the stable preview target if available, since dnd-kit `over` can be noisy.
        const stored =
            titleDragCommitRef.current?.containerId === String(toContainerId)
                ? titleDragCommitRef.current.targetBaseIndex
                : null
        if (typeof stored === 'number' && stored > 0) return stored

        // Fallback: use the last reply we actually hovered during preview (lastOverRef),
        // mapped back into the drag-start base order.
        try {
            const lastHoverId = lastOverRef.current?.overId
            const base = Array.isArray(active?.initialItemIds) ? active.initialItemIds : null
            if (
                lastHoverId &&
                base &&
                lastHoverId !== activeId &&
                !registryRef.current.has(String(lastHoverId)) &&
                !String(lastHoverId).startsWith(TITLE_SLOT_PREFIX)
            ) {
                const ix = base.indexOf(String(lastHoverId))
                if (ix > 0) return ix
            }
        } catch {}

        // Last-resort: interpret the raw `over` signal.
        if (overType === 'titleSlot' || (titleItemId && rawOverId === titleItemId)) return 0
        return -1
    }

    const getTitleOverlapPx = (titleRect, activeRect) => {
        try {
            if (!titleRect || !activeRect) return 0
            const overlap =
                Math.min(activeRect.bottom, titleRect.bottom) -
                Math.max(activeRect.top, titleRect.top)
            return Math.max(0, overlap)
        } catch {
            return 0
        }
    }
    const panelContainerRef = useRef(null)
    const scrollContainerRef = useRef(null)

    const isTitleSwapDebugEnabled = () => {
        try {
            return typeof window !== 'undefined' && !!window.__KNOV_DND_DEBUG_TITLE_SWAP
        } catch {
            return false
        }
    }

    const debugTitleSwapLog = (sig, payload) => {
        if (!isTitleSwapDebugEnabled()) return
        try {
            const now = Date.now()
            // Log when state changes, or at most ~5 times/sec.
            if (
                debugTitleSwapRef.current.lastSig !== sig ||
                now - debugTitleSwapRef.current.lastAt > 200
            ) {
                debugTitleSwapRef.current.lastSig = sig
                debugTitleSwapRef.current.lastAt = now
                // eslint-disable-next-line no-console
                console.log('[dnd:title-swap]', payload)
            }
        } catch {}
    }

    // Debugging for indexing bugs (esp. "title + 1" lists where dropping at end can be ambiguous).
    // Enabled via the existing title swap flag to avoid introducing a new dev toggle.
    const isIndexDebugEnabled = () => {
        try {
            return typeof window !== 'undefined' && !!window.__KNOV_DND_DEBUG_TITLE_SWAP
        } catch {
            return false
        }
    }

    const debugIndexLog = (sig, payload) => {
        if (!isIndexDebugEnabled()) return
        try {
            const now = Date.now()
            // Keep this very low-noise: only log state changes, or at most ~6 times/sec.
            if (debugIndexRef.current.lastSig !== sig || now - debugIndexRef.current.lastAt > 160) {
                debugIndexRef.current.lastSig = sig
                debugIndexRef.current.lastAt = now
                // eslint-disable-next-line no-console
                console.log('[dnd:index]', payload)
            }
        } catch {}
    }

    const pointerMovedSince = (from, to, thresholdPx = 4) => {
        try {
            if (from?.x == null || from?.y == null || to?.x == null || to?.y == null) return false
            const dx = Math.abs(to.x - from.x)
            const dy = Math.abs(to.y - from.y)
            return dx + dy > thresholdPx
        } catch {
            return false
        }
    }

    const isPointerInRect = (pointer, rect, marginPx = 0) => {
        try {
            if (!pointer || pointer.x == null || pointer.y == null || !rect) return false
            const m = Number(marginPx) || 0
            return (
                pointer.x >= rect.left - m &&
                pointer.x <= rect.right + m &&
                pointer.y >= rect.top - m &&
                pointer.y <= rect.bottom + m
            )
        } catch {
            return false
        }
    }

    const registerContainer = useCallback((containerId, api) => {
        registryRef.current.set(String(containerId), api)
        return () => registryRef.current.delete(String(containerId))
    }, [])

    const findContainerForItem = useCallback(itemId => {
        const idStr = String(itemId)
        for (const [containerId, api] of Array.from(registryRef.current.entries())) {
            const ids = api.getItemIds?.() || []
            if (ids.includes(idStr)) return containerId
        }
        return null
    }, [])

    const [active, setActive] = useState(null) // { id, data, initialContainerId, initialIndex }
    const [activeSize, setActiveSize] = useState(null)
    const prevOverscrollRef = useRef(null)
    const lastAutoSwipeAtRef = useRef(0)
    const prevSwiperTouchRef = useRef(null)

    // Desktop: start drag quickly after a small move.
    // Mobile: require a long-press on the grip before drag activates.
    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: { distance: 6 },
        }),
        useSensor(TouchSensor, {
            activationConstraint: { delay: 350, tolerance: 6 },
        }),
    )

    // Make "live re-order" feel more responsive:
    // - Prefer pointer-based collisions (triggers once the pointer enters another item)
    // - Fallback to closestCenter for cases where pointerWithin yields no hits (e.g. fast drags)
    const collisionDetection = useCallback(args => {
        const pointerCollisions = pointerWithin(args)
        if (pointerCollisions?.length) return pointerCollisions
        return closestCenter(args)
    }, [])

    const cleanupOverscroll = () => {
        if (typeof document !== 'undefined' && document.body) {
            document.body.style.overscrollBehavior =
                prevOverscrollRef.current !== null ? prevOverscrollRef.current : ''
            prevOverscrollRef.current = null
        }
    }

    const setGlobalDragging = isDragging => {
        try {
            if (typeof window !== 'undefined') {
                window.isDragging = !!isDragging
                if (typeof window.dispatchEvent === 'function') {
                    window.dispatchEvent(
                        new CustomEvent(GLOBAL_DRAGGING_EVENT, {
                            detail: { isDragging: !!isDragging },
                        }),
                    )
                }
            }
        } catch {}
    }

    const resetAllContainers = () => {
        for (const api of Array.from(registryRef.current.values())) {
            api.resetAnswers?.()
        }
    }

    const handleDragStart = event => {
        setGlobalDragging(true)
        const activeId = event.active?.id
        const data = event.active?.data?.current || {}
        const containerId = data.containerId || findContainerForItem(activeId)
        const api = containerId ? registryRef.current.get(String(containerId)) : null
        const initialIndex = api?.getItemIds?.()?.indexOf(String(activeId)) ?? -1
        const initialItemIds = api?.getItemIds?.() || null

        // Determine "title-ness" from the quest container itself at drag start so permissions
        // update immediately after drops that change which answer is the quest title.
        const draggedAnswerId = data.answerId || data.answer?.id || activeId
        const isTitleAtStart =
            api?.parentAnswerId != null &&
            draggedAnswerId != null &&
            String(api.parentAnswerId) === String(draggedAnswerId)
        const normalizedData = {
            ...data,
            isTitle: !!(isTitleAtStart || data.isTitle),
        }

        setActive({
            id: String(activeId),
            data: normalizedData,
            initialContainerId: containerId ? String(containerId) : null,
            initialIndex,
            initialItemIds,
        })
        titleDragCommitRef.current = { containerId: null, targetBaseIndex: null }

        const rect = event.active?.rect?.current?.initial
        if (rect?.width && rect?.height) setActiveSize({ width: rect.width, height: rect.height })
        else setActiveSize(null)

        // Suppress pull-to-refresh / native overscroll during drag.
        if (
            typeof document !== 'undefined' &&
            document.body &&
            prevOverscrollRef.current === null
        ) {
            prevOverscrollRef.current = document.body.style.overscrollBehavior
            document.body.style.overscrollBehavior = 'contain'
        }
    }

    const handleDragCancel = () => {
        setActive(null)
        setActiveSize(null)
        lastOverRef.current = { overId: null, containerId: null }
        titleDragCommitRef.current = { containerId: null, targetBaseIndex: null }
        titleHoverPointerRef.current = { x: null, y: null }
        sortedToTitleRectRef.current = { containerId: null, rect: null }
        sortedToTitleSwapRef.current = {
            active: false,
            containerId: null,
            baseIds: null,
            enteredPointer: { x: null, y: null },
            enteredActiveTop: null,
        }
        cleanupOverscroll()
        resetAllContainers()
        setGlobalDragging(false)
    }

    // While dragging on mobile, disable Swiper's native touch swiping so it doesn't fight the drag gesture.
    React.useEffect(() => {
        if (!isMobile) return
        const swiper = getSwiperRef()?.current
        if (!swiper || swiper.destroyed) return

        // When a drag is active, lock swiper; otherwise restore.
        if (active) {
            try {
                if (!prevSwiperTouchRef.current) {
                    prevSwiperTouchRef.current = {
                        allowTouchMove: swiper.allowTouchMove,
                        allowSlidePrev: swiper.allowSlidePrev,
                        allowSlideNext: swiper.allowSlideNext,
                    }
                }
                swiper.allowTouchMove = false
                swiper.allowSlidePrev = true
                swiper.allowSlideNext = true
            } catch {}
            return
        }

        // Restore when drag ends.
        try {
            if (prevSwiperTouchRef.current) {
                swiper.allowTouchMove = prevSwiperTouchRef.current.allowTouchMove
                swiper.allowSlidePrev = prevSwiperTouchRef.current.allowSlidePrev
                swiper.allowSlideNext = prevSwiperTouchRef.current.allowSlideNext
                prevSwiperTouchRef.current = null
            }
        } catch {}
    }, [active])

    // Mobile UX: while dragging, auto-swipe panels when the finger/pointer is near the left/right edge.
    // This makes it feasible to drag an answer into a different quest (adjacent panel) without dropping first.
    React.useEffect(() => {
        if (!active) return

        // Mobile: use viewport edges.
        const MOBILE_LEFT_EDGE_PX = 10
        const MOBILE_RIGHT_EDGE_PX = 40
        // Desktop (one-panel mode): use "gutters" directly adjacent to the visible middle panel.
        const DESKTOP_ONEPANEL_GUTTER_PX = 40
        // Desktop (3-panel mode): use viewport edges.
        const DESKTOP_EDGE_PX = 40
        const COOLDOWN_MS = 700

        const getClientX = ev => {
            try {
                // PointerEvent
                if (ev?.clientX != null) return ev.clientX
                // TouchEvent
                if (ev?.touches?.[0]?.clientX != null) return ev.touches[0].clientX
                if (ev?.changedTouches?.[0]?.clientX != null) return ev.changedTouches[0].clientX
            } catch {}
            return null
        }

        const maybeAutoSwipe = rawEv => {
            const x = getClientX(rawEv)
            if (x == null) return

            const now = Date.now()
            if (now - lastAutoSwipeAtRef.current < COOLDOWN_MS) return

            const w = typeof window !== 'undefined' ? window.innerWidth || 0 : 0
            if (!w) return

            const swiper = getSwiperRef()?.current
            if (!swiper) return

            const numPanels = useStore.getState().numPanels
            const onePanelDesktop = !isMobile && numPanels === 1
            const threePanelDesktop = !isMobile && numPanels === 3
            const showSidebar = !!useStore.getState().showSidebar

            // Compute swipe zones.
            let shouldPrev = false
            let shouldNext = false

            if (isMobile) {
                // Mobile: trigger near viewport edges
                shouldPrev = x <= MOBILE_LEFT_EDGE_PX
                shouldNext = x >= w - MOBILE_RIGHT_EDGE_PX
            } else if (onePanelDesktop) {
                // Desktop one-panel: trigger in gutters directly left/right of the active (middle) panel
                try {
                    const slideEl = swiper.slides?.[swiper.activeIndex]
                    const panelEl =
                        slideEl?.querySelector?.('.panel-container-comp') ||
                        slideEl?.querySelector?.('.transform-container') ||
                        null
                    const rect = panelEl?.getBoundingClientRect?.()
                    if (rect) {
                        shouldPrev = x >= rect.left - DESKTOP_ONEPANEL_GUTTER_PX && x <= rect.left
                        shouldNext = x >= rect.right && x <= rect.right + DESKTOP_ONEPANEL_GUTTER_PX
                    } else {
                        // Fallback to viewport edges if we can't locate the panel rect
                        shouldPrev = x <= DESKTOP_EDGE_PX
                        shouldNext = x >= w - DESKTOP_EDGE_PX
                    }
                } catch {
                    shouldPrev = x <= DESKTOP_EDGE_PX
                    shouldNext = x >= w - DESKTOP_EDGE_PX
                }
            } else if (threePanelDesktop && showSidebar) {
                // Desktop 3-panel *with left sidebar open*:
                // Create a swipe zone immediately to the left of the middle panel (since the viewport left edge
                // is covered by the sidebar). Right side can stay as viewport edge.
                try {
                    const centerIx = Math.min(
                        Math.max(0, (swiper.activeIndex || 0) + 1),
                        (swiper.slides?.length || 1) - 1,
                    )
                    const slideEl = swiper.slides?.[centerIx]
                    const panelEl =
                        slideEl?.querySelector?.('.panel-container-comp') ||
                        slideEl?.querySelector?.('.transform-container') ||
                        null
                    const rect = panelEl?.getBoundingClientRect?.()
                    if (rect) {
                        shouldPrev = x >= rect.left - DESKTOP_EDGE_PX && x <= rect.left
                    } else {
                        shouldPrev = x <= DESKTOP_EDGE_PX
                    }
                } catch {
                    shouldPrev = x <= DESKTOP_EDGE_PX
                }
                shouldNext = x >= w - DESKTOP_EDGE_PX
            } else {
                // Desktop 3-panel: trigger near viewport edges
                shouldPrev = x <= DESKTOP_EDGE_PX
                shouldNext = x >= w - DESKTOP_EDGE_PX
            }

            // DnD cross-container rules still apply; this just reveals adjacent containers.
            if (shouldPrev && !swiper.isBeginning) {
                lastAutoSwipeAtRef.current = now
                lastOverRef.current = { overId: null, containerId: null }
                if (!isMobile && useStore.getState().numPanels === 1) {
                    slideToPanel(getCenterIndex() - 1, SWIPE_SPEED_SLOW)
                } else {
                    swiper.slidePrev(SWIPE_SPEED_SLOW)
                }
                return
            }
            if (shouldNext && !swiper.isEnd) {
                lastAutoSwipeAtRef.current = now
                lastOverRef.current = { overId: null, containerId: null }
                if (!isMobile && useStore.getState().numPanels === 1) {
                    slideToPanel(getCenterIndex() + 1, SWIPE_SPEED_SLOW)
                } else {
                    swiper.slideNext(SWIPE_SPEED_SLOW)
                }
            }
        }

        // Use window listeners so we continue tracking even if the DragOverlay is outside the scroll container.
        const onPointerMove = ev => maybeAutoSwipe(ev)
        const onTouchMove = ev => maybeAutoSwipe(ev)

        window.addEventListener('pointermove', onPointerMove, { passive: true })
        window.addEventListener('touchmove', onTouchMove, { passive: true })

        return () => {
            window.removeEventListener('pointermove', onPointerMove)
            window.removeEventListener('touchmove', onTouchMove)
        }
    }, [active])

    // Track the real pointer position during drags (needed to distinguish DOM/layout jitter
    // from actual pointer movement when doing live title-swap previews).
    React.useEffect(() => {
        if (!active) return

        const getClientXY = ev => {
            try {
                if (ev?.clientX != null && ev?.clientY != null)
                    return { x: ev.clientX, y: ev.clientY }
                if (ev?.touches?.[0]?.clientX != null && ev?.touches?.[0]?.clientY != null)
                    return { x: ev.touches[0].clientX, y: ev.touches[0].clientY }
                if (
                    ev?.changedTouches?.[0]?.clientX != null &&
                    ev?.changedTouches?.[0]?.clientY != null
                )
                    return { x: ev.changedTouches[0].clientX, y: ev.changedTouches[0].clientY }
            } catch {}
            return null
        }

        const onPointerMove = ev => {
            const p = getClientXY(ev)
            if (!p) return
            pointerPosRef.current = p
        }
        const onTouchMove = ev => {
            const p = getClientXY(ev)
            if (!p) return
            pointerPosRef.current = p
        }

        window.addEventListener('pointermove', onPointerMove, { passive: true })
        window.addEventListener('touchmove', onTouchMove, { passive: true })
        return () => {
            window.removeEventListener('pointermove', onPointerMove)
            window.removeEventListener('touchmove', onTouchMove)
        }
    }, [active])

    const handleDragOver = event => {
        const { active: activeEvt, over } = event
        if (!activeEvt) return

        const activeId = String(activeEvt.id)
        const activeData = activeEvt.data?.current || {}
        // IMPORTANT: `activeEvt.data.current` can change during a drag as the list re-renders
        // (e.g. after we locally reorder items). For title answers, we must treat "isTitle"
        // as the value from drag start, not the current render index.
        const isTitleDrag = !!(active?.data?.isTitle ?? activeData.isTitle)

        // Keep an origin container fixed for the whole drag so cross-quest rules continue to apply
        // even after hover-based moves insert the placeholder into a new quest.
        const originContainerId =
            active?.initialContainerId ||
            active?.data?.containerId ||
            activeData.containerId ||
            null

        // When we do live title-swap previews, we mutate the list while dragging.
        // During those DOM transitions, dnd-kit may briefly report `over=null`.
        // Treat that as "no new information" and do nothing (avoids oscillating between
        // baseline and swapped orders).
        if (!over) return

        const overId = String(over.id)

        // "Current" container can change as we move the item between quests during hover.
        const fromContainerId = findContainerForItem(activeId) || activeData.containerId
        let toContainerId = null
        let overType = 'item' // 'item' | 'container' | 'titleSlot'

        // IMPORTANT: item ids may also start with "quest-" (e.g. `quest-123:456`), so
        // we must detect containers by registry membership, not by string prefix.
        if (registryRef.current.has(overId)) {
            toContainerId = overId
            overType = 'container'
        } else if (String(overId).startsWith(TITLE_SLOT_PREFIX)) {
            toContainerId = String(overId).slice(TITLE_SLOT_PREFIX.length)
            overType = 'titleSlot'
        } else {
            toContainerId = findContainerForItem(overId)
            overType = 'item'
        }

        if (!fromContainerId || !toContainerId) return
        const isCrossQuestByOrigin =
            originContainerId != null && String(originContainerId) !== String(toContainerId)

        const toApiForTitleTarget = registryRef.current.get(String(toContainerId))
        const titleItemId =
            toApiForTitleTarget?.parentAnswerId != null
                ? `${String(toContainerId)}:${String(toApiForTitleTarget.parentAnswerId)}`
                : null
        const inSortedToTitleMode =
            sortedToTitleSwapRef.current.active &&
            sortedToTitleSwapRef.current.containerId === String(toContainerId) &&
            sortedToTitleRectRef.current?.containerId === String(toContainerId) &&
            !!sortedToTitleRectRef.current?.rect

        // IMPORTANT: while doing sorted->title swap previews, the title row content can change and cause
        // rect jitter. Lock the title rect snapshot for the duration of swap mode to avoid boundary flicker.
        const titleRect =
            (inSortedToTitleMode ? sortedToTitleRectRef.current.rect : null) ||
            toApiForTitleTarget?.getTitleSlotRect?.() ||
            null
        const p = pointerPosRef.current
        const activeTranslatedRect = event?.active?.rect?.current?.translated || null
        const activeTranslatedTop = activeTranslatedRect?.top
        const pointerX = pointerPosRef.current?.x
        const pointerY = pointerPosRef.current?.y

        // Title zone signals:
        // - Primary: overlap between drag overlay rect and title-slot rect (matches user perception)
        // - Fallback: pointer vs title-slot bottom, for cases where overlay rect isn't available

        const titleOverlapPx = getTitleOverlapPx(titleRect, activeTranslatedRect)
        const ghostOverlapsTitleEnter = titleOverlapPx >= TITLE_ZONE.enterOverlapPx
        const ghostOverlapsTitleStay = titleOverlapPx >= TITLE_ZONE.stayOverlapPx
        const ghostFullyBelowTitle =
            !!titleRect &&
            !!activeTranslatedRect &&
            titleOverlapPx <= 0 &&
            typeof activeTranslatedRect.top === 'number' &&
            typeof titleRect.bottom === 'number' &&
            activeTranslatedRect.top > titleRect.bottom + 2

        // Short-circuit fallback: prefer overlap-only behavior.
        // If we don't have overlay rect overlap info, we treat it as "not in title zone"
        // (instead of falling back to pointer/geometry heuristics).
        const geomInTitleZone = ghostOverlapsTitleStay

        const geomEnterTitleZone = ghostOverlapsTitleEnter
        // Hysteresis:
        // - enter uses tight rect (0 margin)
        // - stay uses small margin to avoid boundary jitter, but not so large that you can't exit
        const pointerInTitleRectTight = isPointerInRect(p, titleRect, 0)
        const pointerInTitleRectLoose = isPointerInRect(p, titleRect, 6)
        // IMPORTANT: For *sorted -> title* swap previews:
        // - Allow ENTERING swap mode when hovering the title item or the title-slot overlay.
        // - But do NOT treat "hovering the title item" as *staying* in the swap target, because the
        //   title row can be very tall (expanded content) and that makes swap mode too sticky.
        const isTitleSwapZone =
            pointerInTitleRectTight ||
            overType === 'titleSlot' ||
            ghostOverlapsTitleEnter ||
            // Allow title-item collision to ENTER swap mode only when we're geometrically near the title.
            (overType === 'item' && titleItemId && overId === titleItemId && geomInTitleZone)
        const isTitleTarget =
            pointerInTitleRectLoose || overType === 'titleSlot' || ghostOverlapsTitleStay

        // Debug logs are emitted only on state transitions (apply swap, revert swap, resume list mode).

        const baseIds =
            active?.initialContainerId === String(toContainerId) &&
            Array.isArray(active?.initialItemIds)
                ? active.initialItemIds
                : null

        // Title answers are the "head" of a quest; they can be reordered within their own quest
        // but should never be moved into a different quest. If we allow hover-based cross-container
        // moves, dnd-kit will show a placeholder in other quests, which is misleading.
        if (isTitleDrag && isCrossQuestByOrigin) return

        // The title has its own swap-mode target area. For best UX we rely on the title row rect
        // (pointerInTitleRect) rather than dnd-kit's `over` alone, since `over` can bounce between
        // title-slot/title-item/container, especially when dragging the first sorted answer upward.
        // Sorted -> title swap preview should win over normal list reorder.
        // If we are geometrically in the title zone, do NOT let standard sortable reorder run;
        // instead, enter/maintain swap-preview so the title appears in the dragged answer's original slot.
        if (!isTitleDrag && !isCrossQuestByOrigin && (isTitleSwapZone || geomEnterTitleZone)) {
            // Also disallow dropping onto another quest's title.
            const toApi = toApiForTitleTarget
            if (!toApi) return

            // IMPORTANT: if we're hovering over ourselves (overId === activeId), do NOT enter swap mode
            // or apply swap. This can happen at the start of a drag when the first sorted answer is
            // near the title zone. We only swap when we have a real title item/slot collision.
            // However, when we use geometry to detect being in the title zone, `overId` may stay as activeId.
            // In that case, we still want to enter swap mode, but we should not apply swap until we have
            // a stable baseline and a valid baseIndex.

            // Swap-preview for dragging a sorted answer onto the title:
            // Snapshot the current order when we first enter swap mode, and restore that snapshot when leaving.
            const currentIds = toApi.getItemIds?.() || []
            if (!currentIds?.length) return

            // Enter swap mode when we're clearly in the title region:
            // - explicit title-slot
            // - pointer is within the overlay rect
            // - OR we collided with the title item itself
            // We allow title item collision only for ENTERING; staying/exit is governed by `isTitleTarget`
            // (overlay-based) to avoid stickiness.
            const hasTitleCollision =
                overType === 'titleSlot' ||
                pointerInTitleRectTight ||
                ghostOverlapsTitleEnter ||
                geomEnterTitleZone ||
                (titleItemId && overId === titleItemId && geomInTitleZone)
            const shouldEnter =
                !sortedToTitleSwapRef.current.active ||
                sortedToTitleSwapRef.current.containerId !== String(toContainerId)
            if (shouldEnter && hasTitleCollision) {
                // Prefer the drag-start snapshot as the swap base so we don't "treat the whole thing as a list"
                // near the title zone. If we've already locally reordered for any reason, currentIds is no longer
                // a stable baseline and swap-preview will look wrong for 2nd/3rd answers.
                const swapBaseForSnapshot =
                    Array.isArray(baseIds) && baseIds.length && baseIds.includes(activeId)
                        ? baseIds
                        : currentIds
                sortedToTitleSwapRef.current = {
                    active: true,
                    containerId: String(toContainerId),
                    baseIds: swapBaseForSnapshot,
                    enteredPointer: {
                        x: pointerPosRef.current?.x ?? null,
                        y: pointerPosRef.current?.y ?? null,
                    },
                    enteredActiveTop:
                        typeof activeTranslatedTop === 'number' ? activeTranslatedTop : null,
                }
            }
            if (
                !sortedToTitleSwapRef.current.active ||
                sortedToTitleSwapRef.current.containerId !== String(toContainerId)
            ) {
                // Not yet in swap mode; wait until we enter the title zone.
                return
            }

            const swapBaseIds = sortedToTitleSwapRef.current.baseIds || currentIds
            const baseIndex = swapBaseIds.indexOf(activeId)
            // If we don't have the activeId in the snapshot (should be rare), avoid corrupting the list.
            // But still block normal reorder while we're in the title zone.
            if (baseIndex <= 0) return

            if (
                !sortedToTitleRectRef.current.rect ||
                sortedToTitleRectRef.current.containerId !== String(toContainerId)
            ) {
                sortedToTitleRectRef.current = {
                    containerId: String(toContainerId),
                    rect: titleRect,
                }
            }

            const desiredIds = [...swapBaseIds]
            const tmp = desiredIds[0]
            desiredIds[0] = desiredIds[baseIndex]
            desiredIds[baseIndex] = tmp

            const same =
                currentIds.length === desiredIds.length &&
                currentIds.every((id, i) => String(id) === String(desiredIds[i]))
            if (same) return

            debugTitleSwapLog(
                ['apply-sorted-to-title', activeId, toContainerId, overType].join('|'),
                {
                    action: 'setItemsOrderLocally',
                    mode: 'sorted->title',
                    activeId,
                    toContainerId,
                    overId,
                    overType,
                    titleItemId,
                    baseIndex,
                    baseIds: swapBaseIds.slice(0, 10),
                    desiredIds: desiredIds.slice(0, 10),
                    currentIds: currentIds.slice(0, 10),
                },
            )

            lastOverRef.current = { overId, containerId: toContainerId }
            toApi.setItemsOrderLocally?.(desiredIds)
            return
        }

        // For title drags within the same quest, live-swap the title placeholder with the
        // first collided non-title item. Key difference: we do NOT trust `overId` alone here,
        // because after we reorder the DOM, `over` can briefly become the container or the active item.
        // Instead, derive the target from `event.collisions` and only revert when there are no
        // non-title item collisions.
        if (isTitleDrag && !isCrossQuestByOrigin) {
            const toApi = registryRef.current.get(String(toContainerId))
            if (!toApi) return

            if (!baseIds?.length) return

            // If we're hovering the explicit title-slot while dragging the title, treat that as
            // "return title to the top" (revert preview to baseline).
            if (overType === 'titleSlot') {
                const currentIds = toApi.getItemIds?.() || []
                const sameAsBase =
                    currentIds.length === baseIds.length &&
                    currentIds.every((id, i) => String(id) === String(baseIds[i]))
                if (!sameAsBase) {
                    debugTitleSwapLog(['revert-title-home', activeId, toContainerId].join('|'), {
                        action: 'revertToBase',
                        mode: 'title->sorted',
                        activeId,
                        toContainerId,
                    })
                    toApi.setItemsOrderLocally?.(baseIds)
                }
                return
            }

            const collisions = Array.isArray(event?.collisions) ? event.collisions : []
            let hoverTargetId = null
            for (const c of collisions) {
                const cid = String(c?.id || '')
                if (!cid) continue
                if (cid === activeId) continue
                if (registryRef.current.has(cid)) continue
                if (cid.startsWith(TITLE_SLOT_PREFIX)) continue
                const ix = baseIds.indexOf(cid)
                if (ix > 0) {
                    hoverTargetId = cid
                    break
                }
            }

            if (!hoverTargetId) {
                // IMPORTANT: After we apply a live swap, dnd-kit often emits DragOver events where
                // `overType === 'container'` due to layout/DOM jitter. If we revert on those frames,
                // we oscillate. Only revert when the *real pointer* moved away from the last hovered item
                // and we still have no valid item collision (i.e. user actually moved into empty space).
                const p = pointerPosRef.current
                const hp = titleHoverPointerRef.current
                const pointerMoved = pointerMovedSince(hp, p, 4)
                if (pointerMoved && overType === 'container' && overId === String(toContainerId)) {
                    const currentIds = toApi.getItemIds?.() || []
                    const sameAsBase =
                        currentIds.length === baseIds.length &&
                        currentIds.every((id, i) => String(id) === String(baseIds[i]))
                    if (!sameAsBase) {
                        debugTitleSwapLog(['revert', activeId, toContainerId].join('|'), {
                            action: 'revertToBase',
                            activeId,
                            toContainerId,
                        })
                        toApi.setItemsOrderLocally?.(baseIds)
                    }
                }
                return
            }

            // Record pointer position at the moment we have a stable hover target.
            if (pointerPosRef.current?.x != null && pointerPosRef.current?.y != null) {
                titleHoverPointerRef.current = { ...pointerPosRef.current }
            }

            const baseIndex = baseIds.indexOf(hoverTargetId)
            // Persist target in terms of the drag-start base order (the one Quest.moveAnswer uses).
            if (baseIndex > 0) {
                titleDragCommitRef.current = {
                    containerId: String(toContainerId),
                    targetBaseIndex: baseIndex,
                }
            }
            const desiredIds = [...baseIds]
            const tmp = desiredIds[0]
            desiredIds[0] = desiredIds[baseIndex]
            desiredIds[baseIndex] = tmp

            const currentIds = toApi.getItemIds?.() || []
            const same =
                currentIds.length === desiredIds.length &&
                currentIds.every((id, i) => String(id) === String(desiredIds[i]))
            if (same) return

            debugTitleSwapLog(['apply', activeId, hoverTargetId, toContainerId].join('|'), {
                action: 'setItemsOrderLocally',
                activeId,
                hoverTargetId,
                toContainerId,
                baseIds: baseIds.slice(0, 10),
                desiredIds: desiredIds.slice(0, 10),
                currentIds: currentIds.slice(0, 10),
            })

            lastOverRef.current = { overId: hoverTargetId, containerId: toContainerId }
            toApi.setItemsOrderLocally?.(desiredIds)
            return
        }

        // If we previously applied a sorted->title swap preview and the user drags back into the
        // normal list area, restore the baseline order before resuming normal sortable behavior.
        //
        // Key requirement: this MUST NOT rely on pointer tracking (which can be null / unreliable
        // depending on browser + sensor), otherwise the preview can get "stuck" and block returning
        // the dragged item to its original spot.
        //
        // Instead, exit swap mode as soon as collisions indicate we're over a real non-title item.
        if (!isTitleDrag && !isCrossQuestByOrigin && sortedToTitleSwapRef.current.active) {
            if (sortedToTitleSwapRef.current.containerId !== String(toContainerId)) return

            const stillInTitleRegion =
                isTitleTarget ||
                overType === 'titleSlot' ||
                // Prefer the *ghost* overlap to decide whether we're still in the title zone.
                // Pointer can remain near the boundary on tall answers even after the ghost has clearly
                // entered the sorted list; if we keep swap mode in that case, the placeholder won't
                // resume normal list behavior quickly enough.
                ghostOverlapsTitleStay ||
                // If collisions still say "title item" but geometry says we're not near title anymore,
                // treat that as outside (prevents re-enter/revert oscillation).
                (overType === 'item' && titleItemId && overId === titleItemId && geomInTitleZone)
            if (stillInTitleRegion) return

            const collisions = Array.isArray(event?.collisions) ? event.collisions : []
            let nonTitleHoverId = null
            for (const c of collisions) {
                const cid = String(c?.id || '')
                if (!cid) continue
                if (cid === activeId) continue
                if (registryRef.current.has(cid)) continue
                if (cid.startsWith(TITLE_SLOT_PREFIX)) continue
                if (titleItemId && cid === titleItemId) continue
                nonTitleHoverId = cid
                break
            }
            // If we don't have a stable non-title collision yet (common: dnd-kit reports container/active
            // during DOM transitions), we still need a way to exit swap mode deterministically so users can
            // drag the item back to its original spot.
            //
            const movedDownFromEntry =
                typeof activeTranslatedTop === 'number' &&
                typeof sortedToTitleSwapRef.current.enteredActiveTop === 'number'
                    ? activeTranslatedTop - sortedToTitleSwapRef.current.enteredActiveTop
                    : null
            // Exit swap mode once we are no longer in the title zone and we have either:
            // - a clear non-title item collision (back in list mode), OR
            // - the ghost fully below the title rect (even if collisions are noisy).
            if (!ghostFullyBelowTitle && !nonTitleHoverId) return
            // Hysteresis to avoid immediate enter->exit oscillation from layout jitter:
            // require a meaningful downward move since swap mode entry.
            if (
                !nonTitleHoverId &&
                ghostFullyBelowTitle &&
                movedDownFromEntry != null &&
                movedDownFromEntry < 12
            ) {
                return
            }

            const toApi = registryRef.current.get(String(toContainerId))
            if (toApi) {
                const currentIds = toApi.getItemIds?.() || []
                const restoreIds = sortedToTitleSwapRef.current.baseIds
                if (Array.isArray(restoreIds) && restoreIds.length) {
                    const sameAsRestore =
                        currentIds.length === restoreIds.length &&
                        currentIds.every((id, i) => String(id) === String(restoreIds[i]))
                    if (!sameAsRestore) {
                        debugTitleSwapLog(
                            [
                                'revert-sorted-to-title',
                                activeId,
                                toContainerId,
                                nonTitleHoverId || 'noItemCollision',
                                ghostFullyBelowTitle ? 'ghostBelow' : 'ghostNotBelow',
                            ].join('|'),
                            {
                                action: 'revertToBase',
                                mode: 'sorted->title',
                                reason: ghostFullyBelowTitle
                                    ? 'ghost-below-title'
                                    : 'collided-with-non-title-item',
                                activeId,
                                toContainerId,
                                nonTitleHoverId,
                                overId,
                                overType,
                                collisions: collisions.slice(0, 8).map(c => String(c?.id)),
                                titleRect: titleRect
                                    ? {
                                          top: titleRect.top,
                                          bottom: titleRect.bottom,
                                          left: titleRect.left,
                                          right: titleRect.right,
                                      }
                                    : null,
                                pointer: p,
                                titleOverlapPx,
                                ghostOverlapsTitleEnter,
                                ghostOverlapsTitleStay,
                                ghostFullyBelowTitle,
                                activeTranslatedRect: activeTranslatedRect
                                    ? {
                                          top: activeTranslatedRect.top,
                                          bottom: activeTranslatedRect.bottom,
                                          left: activeTranslatedRect.left,
                                          right: activeTranslatedRect.right,
                                      }
                                    : null,
                                movedDownFromEntry,
                            },
                        )
                        toApi.setItemsOrderLocally?.(restoreIds)

                        // Critical UX: once we exit swap mode back into normal sorted_answers mode,
                        // immediately move the active placeholder into the hovered slot (if we have one).
                        // Otherwise the list can appear "stuck" in base order until the next DragOver tick.
                        //
                        // This makes the placeholder appear under the ghost right away and pushes items down.
                        try {
                            if (nonTitleHoverId) {
                                const fromIndex = restoreIds.indexOf(activeId)
                                const toIndex = restoreIds.indexOf(nonTitleHoverId)
                                if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
                                    debugTitleSwapLog(
                                        [
                                            'resume-list-mode',
                                            activeId,
                                            toContainerId,
                                            fromIndex,
                                            toIndex,
                                        ].join('|'),
                                        {
                                            action: 'replaceItemsLocally',
                                            mode: 'resume-list-after-swap',
                                            activeId,
                                            toContainerId,
                                            nonTitleHoverId,
                                            fromIndex,
                                            toIndex,
                                        },
                                    )
                                    toApi.replaceItemsLocally?.(fromIndex, toIndex)
                                    lastOverRef.current = {
                                        overId: nonTitleHoverId,
                                        containerId: toContainerId,
                                    }
                                }
                            }
                        } catch {}
                    }
                }
            }

            sortedToTitleSwapRef.current = {
                active: false,
                containerId: null,
                baseIds: null,
                enteredPointer: { x: null, y: null },
                enteredActiveTop: null,
            }
            sortedToTitleRectRef.current = { containerId: null, rect: null }
            return
        }

        // If sorted->title swap mode is active, never fall through into the normal reorder logic.
        if (!isTitleDrag && !isCrossQuestByOrigin && sortedToTitleSwapRef.current.active) {
            return
        }

        // Guardrail: while the pointer is near the title zone, do NOT run the normal sortable
        // reorder logic. This prevents the "title jumps to last spot" corruption that can happen
        // when we just exited swap mode and dnd-kit reports `overType=container`/`overId=activeId`.
        if (
            !isTitleDrag &&
            !isCrossQuestByOrigin &&
            titleRect &&
            // Only apply this guardrail while the ghost is actually intersecting the title zone.
            // Once the ghost re-enters the sorted list (no overlap), we must resume normal list mode
            // immediately so the placeholder sits under the ghost and items shift naturally.
            (ghostOverlapsTitleStay || titleOverlapPx > 0)
        ) {
            return
        }

        // IMPORTANT: dnd-kit will often report `overType='container'` transiently (or even consistently)
        // when the pointer is between items. If we treat that as "move to end", the placeholder jumps
        // to the last spot (exactly the bug being debugged here).
        //
        // For same-container reorders, only treat "container over" as a real signal when the pointer
        // is *near the bottom* of the container (intentional move-to-end gesture). Otherwise, ignore it.
        if (fromContainerId === toContainerId && overType === 'container') {
            const containerRect = over?.rect || null
            const nearBottom =
                p?.y != null &&
                containerRect?.bottom != null &&
                typeof containerRect.bottom === 'number' &&
                p.y > containerRect.bottom - 24
            if (!nearBottom) {
                debugTitleSwapLog(['ignore-container-over', activeId, toContainerId].join('|'), {
                    action: 'ignoreContainerOver',
                    reason: 'same-container-not-near-bottom',
                    activeId,
                    toContainerId,
                    overId,
                    overType,
                    pointer: p,
                    containerRect: containerRect
                        ? {
                              top: containerRect.top,
                              bottom: containerRect.bottom,
                              left: containerRect.left,
                              right: containerRect.right,
                          }
                        : null,
                })
                return
            }
        }

        // Also: for same-container drags, NEVER treat hovering the *title item* as a normal reorder target.
        // That causes the active placeholder to jump into index 0 as a standard reorder, corrupting the
        // baseline order right before we enter sorted->title swap mode.
        if (
            !isTitleDrag &&
            !isCrossQuestByOrigin &&
            fromContainerId === toContainerId &&
            overType === 'item' &&
            titleItemId &&
            overId === titleItemId
        ) {
            debugTitleSwapLog(['ignore-title-item-reorder', activeId, toContainerId].join('|'), {
                action: 'ignoreTitleItemReorder',
                activeId,
                toContainerId,
                overId,
                overType,
                pointer: p,
            })
            return
        }

        // Avoid re-running work if we are still over the same thing.
        if (
            lastOverRef.current.overId === overId &&
            lastOverRef.current.containerId === toContainerId
        ) {
            return
        }
        lastOverRef.current = { overId, containerId: toContainerId }

        const fromApi = registryRef.current.get(String(fromContainerId))
        const toApi = registryRef.current.get(String(toContainerId))
        if (!fromApi || !toApi) return

        // Only allow cross-quest drops if the user can edit the target quest.
        if (isCrossQuestByOrigin && toApi.canDrop === false) {
            return
        }

        // Disallow dragging an answer into its own child quest (cross-container only).
        // (Target quest's parent answer is the dragged answer.)
        if (
            isCrossQuestByOrigin &&
            toApi.parentAnswerId &&
            String(toApi.parentAnswerId) === String(activeData.answerId)
        ) {
            return
        }

        const fromIds = fromApi.getItemIds?.() || []
        const toIds = toApi.getItemIds?.() || []

        // When moving a *non-title* answer across quests, do not allow dropping "on" the target quest's
        // title (index 0). That interaction is confusing and causes placeholders/order updates as if
        // the title were just another sortable item.
        if (!isTitleDrag && isCrossQuestByOrigin && !registryRef.current.has(overId)) {
            const targetTitleDndId =
                toApi.parentAnswerId != null
                    ? `${String(toContainerId)}:${String(toApi.parentAnswerId)}`
                    : null
            // If the target quest has *no* sorted answers (title-only), the title is the only thing
            // dnd-kit can collide with. In that case, allow dropping "on" the title and interpret it
            // as "insert after title".
            const targetIsTitleOnly = (toIds?.length || 0) <= 1
            if (!targetIsTitleOnly) {
                if (targetTitleDndId && String(targetTitleDndId) === overId) return
                // Fallback: block "first item" if parentAnswerId isn't available for some reason.
                const targetFirstId = toIds?.[0]
                if (targetFirstId && String(targetFirstId) === overId) return
            }
        }

        const fromIndex = fromIds.indexOf(activeId)
        const overIndex = registryRef.current.has(overId) ? toIds.length : toIds.indexOf(overId)
        // Compute insertion index.
        // UX fix: when hovering the *last* item and the pointer is in its lower half,
        // treat as "insert after" (i.e. drop at end). This matters especially when
        // there is only one sorted answer (title + 1).
        let toIndex = overIndex === -1 ? toIds.length : overIndex
        try {
            if (overType === 'item' && overIndex !== -1 && overIndex === toIds.length - 1) {
                const pointerY = pointerPosRef.current?.y
                const overRect = over?.rect || null
                const insertAfter =
                    pointerY != null &&
                    overRect?.top != null &&
                    overRect?.height != null &&
                    pointerY > overRect.top + overRect.height / 2
                if (insertAfter) toIndex = Math.min(overIndex + 1, toIds.length)

                // Targeted logs for the "sorted_answers length 1" case:
                // If the list is effectively [title, onlyReply], we want to see whether we computed
                // "insert after" when the user drops near the bottom of the only reply.
                if (toIds.length <= 2) {
                    debugIndexLog(
                        [
                            'len2',
                            activeId,
                            fromContainerId,
                            toContainerId,
                            overId,
                            overIndex,
                            toIndex,
                            insertAfter ? 'after' : 'before',
                        ].join('|'),
                        {
                            action: 'sameQuestIndexing',
                            note: 'title+1 list (toIds.length<=2)',
                            activeId,
                            overId,
                            overType,
                            fromContainerId,
                            toContainerId,
                            fromIndex,
                            overIndex,
                            toIndex,
                            insertAfter,
                            pointer: { ...pointerPosRef.current },
                            overRect: overRect
                                ? {
                                      top: overRect.top,
                                      height: overRect.height,
                                      bottom: overRect.bottom,
                                  }
                                : null,
                            toIds: toIds.slice(0, 3),
                        },
                    )
                }
            }
        } catch {}

        if (fromContainerId === toContainerId) {
            if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
                toApi.replaceItemsLocally?.(fromIndex, toIndex)
            }
            return
        }

        // Cross-container: move visually between lists.
        if (fromIndex === -1) return
        const answer = activeData.answer
        if (!answer) return

        fromApi.deleteLocalItem?.(fromIndex)
        // Keep the dragged item's DnD id stable even after inserting into a different quest
        // during hover-based moves.
        toApi.insertLocalItem?.(toIndex, { ...answer, __knovDndId: activeId })
    }

    const handleDragEnd = event => {
        const { active: activeEvt, over } = event
        if (!activeEvt) return

        const activeId = String(activeEvt.id)
        const activeData = activeEvt.data?.current || {}
        // Same reasoning as in onDragOver: treat title-ness as fixed at drag start.
        const isTitleDrag = !!(active?.data?.isTitle ?? activeData.isTitle)
        // IMPORTANT: after local reorders, `activeEvt.data.current` can become stale/incorrect.
        // Always prefer the drag-start snapshot for the real answer id we commit to the backend.
        const committedAnswerId =
            active?.data?.answerId ||
            active?.data?.answer?.id ||
            activeData.answerId ||
            activeData.answer?.id ||
            activeId
        const rawOverId = over?.id != null ? String(over.id) : null

        const initialContainerId = active?.initialContainerId || activeData.containerId || null
        let toContainerId = null
        let overType = rawOverId ? 'item' : null // 'item' | 'container' | 'titleSlot'
        if (over?.id) {
            const overId = String(over.id)
            if (registryRef.current.has(overId)) {
                toContainerId = overId
                overType = 'container'
            } else if (String(overId).startsWith(TITLE_SLOT_PREFIX)) {
                toContainerId = String(overId).slice(TITLE_SLOT_PREFIX.length)
                overType = 'titleSlot'
            } else {
                toContainerId = findContainerForItem(overId)
                overType = 'item'
            }
        }
        // If we dropped outside any specific droppable, fall back to where the item ended up
        // after hover-based moves.
        if (!toContainerId) {
            toContainerId = findContainerForItem(activeId)
        }

        // Dropped on nothing: revert.
        if (!toContainerId || !initialContainerId) {
            resetAllContainers()
            // Restore quests that were temporarily altered during cross-container hovers.
            for (const api of Array.from(registryRef.current.values())) {
                api.resetOtherQuest?.(activeData.quest_id || activeData.initialQuestId)
            }
            setActive(null)
            setActiveSize(null)
            lastOverRef.current = { overId: null, containerId: null }
            cleanupOverscroll()
            setGlobalDragging(false)
            return
        }

        // Title answers should never move across quests; revert if the user drops over another quest.
        if (isTitleDrag && initialContainerId !== toContainerId) {
            resetAllContainers()
            setActive(null)
            setActiveSize(null)
            lastOverRef.current = { overId: null, containerId: null }
            cleanupOverscroll()
            setGlobalDragging(false)
            return
        }

        const toApi = registryRef.current.get(String(toContainerId))
        if (!toApi) {
            setActive(null)
            setActiveSize(null)
            lastOverRef.current = { overId: null, containerId: null }
            cleanupOverscroll()
            setGlobalDragging(false)
            return
        }

        const titleItemId =
            toApi.parentAnswerId != null
                ? `${String(toContainerId)}:${String(toApi.parentAnswerId)}`
                : null

        // When dropping a *non-title* answer across quests, disallow dropping "on" the target title item.
        // (Dropping into the quest is still possible by targeting the container/other answers.)
        if (
            !isTitleDrag &&
            initialContainerId !== toContainerId &&
            rawOverId &&
            !registryRef.current.has(rawOverId)
        ) {
            const targetTitleDndId =
                toApi.parentAnswerId != null
                    ? `${String(toContainerId)}:${String(toApi.parentAnswerId)}`
                    : null
            const toIds = toApi.getItemIds?.() || []
            const targetFirstId = toIds?.[0]
            const droppingOnTitle =
                (targetTitleDndId && String(targetTitleDndId) === rawOverId) ||
                (targetFirstId && String(targetFirstId) === rawOverId)
            const targetIsTitleOnly = (toIds?.length || 0) <= 1
            if (droppingOnTitle && !targetIsTitleOnly) {
                resetAllContainers()
                setActive(null)
                setActiveSize(null)
                lastOverRef.current = { overId: null, containerId: null }
                cleanupOverscroll()
                setGlobalDragging(false)
                return
            }
        }

        // Disallow cross-quest drop if user can't edit target quest (revert).
        if (initialContainerId !== toContainerId && toApi.canDrop === false) {
            resetAllContainers()
            setActive(null)
            setActiveSize(null)
            lastOverRef.current = { overId: null, containerId: null }
            cleanupOverscroll()
            setGlobalDragging(false)
            return
        }

        // Disallow dragging an answer into its own child quest (cross-container only, revert).
        if (
            initialContainerId !== toContainerId &&
            toApi.parentAnswerId &&
            String(toApi.parentAnswerId) === String(committedAnswerId)
        ) {
            resetAllContainers()
            setActive(null)
            setActiveSize(null)
            lastOverRef.current = { overId: null, containerId: null }
            cleanupOverscroll()
            setGlobalDragging(false)
            return
        }

        const toIds = toApi.getItemIds?.() || []
        const finalIndex = toIds.indexOf(activeId)

        if (initialContainerId === toContainerId) {
            // commitReorder expects the real answer id (not the dnd id)
            //
            // IMPORTANT: for title drags, `finalIndex` can be misleading after live swap previews
            // (and sometimes ends up 0 even when the user dropped onto a reply). Prefer dropIndex when
            // it points at an actual item target.
            if (isTitleDrag) {
                const intendedIndex = getTitleDragDropIndex({
                    toContainerId,
                    overType,
                    rawOverId,
                    titleItemId,
                    activeId,
                })
                toApi.commitReorder?.(
                    committedAnswerId,
                    intendedIndex !== -1 ? intendedIndex : finalIndex,
                )
            } else if (finalIndex !== -1) {
                // Non-title answers should only be committed to index 0 when the user actually
                // dropped in the title swap zone. dnd-kit can report `over` as the title item/slot
                // transiently (e.g. `closestCenter` fallback) when the pointer is not within any
                // droppable; treating that as "drop onto title" causes the bug where dropping at
                // the end snaps to the beginning.
                const titleRect =
                    toApi?.getTitleSlotRect?.() ||
                    (sortedToTitleRectRef.current?.containerId === String(toContainerId)
                        ? sortedToTitleRectRef.current.rect
                        : null)
                const activeDropRect =
                    event?.active?.rect?.current?.translated ||
                    event?.active?.rect?.current?.initial ||
                    null
                const titleOverlapAtDrop = getTitleOverlapPx(titleRect, activeDropRect)
                const pointerInTitleAtDrop = isPointerInRect(pointerPosRef.current, titleRect, 4)
                const droppedInTitleSwapZone =
                    sortedToTitleSwapRef.current.active &&
                    sortedToTitleSwapRef.current.containerId === String(toContainerId) &&
                    (overType === 'titleSlot' ||
                        titleOverlapAtDrop >= TITLE_ZONE.stayOverlapPx ||
                        pointerInTitleAtDrop)

                if (toIds.length <= 2) {
                    debugIndexLog(
                        [
                            'end:len2',
                            activeId,
                            initialContainerId,
                            toContainerId,
                            rawOverId || 'none',
                            overType || 'none',
                            finalIndex,
                            droppedInTitleSwapZone ? 'title0' : 'final',
                        ].join('|'),
                        {
                            action: 'sameQuestCommit',
                            note: 'title+1 list (toIds.length<=2)',
                            activeId,
                            committedAnswerId,
                            initialContainerId,
                            toContainerId,
                            rawOverId,
                            overType,
                            finalIndex,
                            droppedInTitleSwapZone,
                            commitIndex: droppedInTitleSwapZone ? 0 : finalIndex,
                            toIds: toIds.slice(0, 3),
                        },
                    )
                }

                toApi.commitReorder?.(committedAnswerId, droppedInTitleSwapZone ? 0 : finalIndex)
            } else {
                toApi.commitReorder?.(committedAnswerId, finalIndex)
            }
        } else {
            // Commit cross-quest move via target quest API.
            if (finalIndex !== -1) {
                // IMPORTANT: during cross-container drags we insert a local placeholder into the target list.
                // That makes `finalIndex` an index into the *post-insert* list, which is often off-by-one
                // relative to what `Quest.moveAnswerBetweenQuests(hoverIndex, ...)` expects (it uses
                // the target quest's underlying `displayAnswers()` which does NOT include our placeholder).
                //
                // Compute a stable "hoverIndex" against the target list *without* the active placeholder,
                // and clamp it so "drop at end" never becomes one-past-end.
                let hoverIndexForCommit = finalIndex
                try {
                    const baseIds = (toIds || []).filter(id => String(id) !== String(activeId))
                    const baseLen = baseIds.length

                    // `moveAnswerBetweenQuests(hoverIndex, ...)` expects an index into the target quest's
                    // `displayAnswers()` (which includes the title at index 0).
                    //
                    // Important: inserting "at end" is represented by `hoverIndex === baseLen`
                    // (i.e. one past the last item), so we must allow the commit index to equal `baseLen`.
                    const appendHoverIndex = Math.max(1, baseLen)

                    if (overType === 'container') {
                        hoverIndexForCommit = appendHoverIndex
                    } else if (overType === 'item' && rawOverId) {
                        const targetIx = baseIds.indexOf(String(rawOverId))
                        if (targetIx !== -1) {
                            // Interpret "after vs before" similarly to DragOver: lower half means after.
                            const pointerY = pointerPosRef.current?.y
                            const overRect = over?.rect || null
                            const insertAfter =
                                pointerY != null &&
                                overRect?.top != null &&
                                overRect?.height != null &&
                                pointerY > overRect.top + overRect.height / 2
                            hoverIndexForCommit = Math.min(
                                targetIx + (insertAfter ? 1 : 0),
                                baseLen,
                            )
                            // Never commit into title slot for cross-quest moves.
                            hoverIndexForCommit = Math.max(1, hoverIndexForCommit)
                        } else {
                            hoverIndexForCommit = Math.max(1, Math.min(finalIndex, baseLen))
                        }
                    } else {
                        hoverIndexForCommit = Math.max(1, Math.min(finalIndex, baseLen))
                    }
                } catch {}

                toApi.moveAnswerBetweenQuests?.(hoverIndexForCommit, committedAnswerId)
            }
        }

        setActive(null)
        setActiveSize(null)
        lastOverRef.current = { overId: null, containerId: null }
        titleDragCommitRef.current = { containerId: null, targetBaseIndex: null }
        titleHoverPointerRef.current = { x: null, y: null }
        sortedToTitleRectRef.current = { containerId: null, rect: null }
        sortedToTitleSwapRef.current = {
            active: false,
            containerId: null,
            baseIds: null,
            enteredPointer: { x: null, y: null },
            enteredActiveTop: null,
        }
        cleanupOverscroll()
        setGlobalDragging(false)
    }

    const ctxValue = useMemo(
        () => ({
            registerContainer,
            activeId: active?.id || null,
        }),
        [registerContainer, active?.id],
    )

    return (
        <AnswersDndRegistryContext.Provider value={ctxValue}>
            <DndContext
                sensors={sensors}
                collisionDetection={collisionDetection}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                {children}

                {typeof document !== 'undefined' &&
                    createPortal(
                        <DragOverlay dropAnimation={null}>
                            {active ? (
                                <div
                                    style={{
                                        width: activeSize?.width,
                                        // Clamp the drag "ghost" height so payloads (images/videos/etc.)
                                        // don't create a huge overlay that feels janky while dragging.
                                        maxHeight: 200,
                                        height: activeSize?.height
                                            ? Math.min(activeSize.height, 200)
                                            : undefined,
                                        overflow: 'hidden',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {(() => {
                                        const panelId = active.data?.panelId
                                        const panel = panelId
                                            ? useStore.getState().panels.getPanel(panelId)
                                            : undefined
                                        const ghost = active.data?.renderGhost?.()

                                        // Some answer subcomponents (e.g. ThinParent) require PanelContext.
                                        // The overlay is outside any specific PanelContainer, so we provide
                                        // a minimal PanelContext here.
                                        if (!panelId || !panel) return ghost

                                        return (
                                            <PanelContextProvider
                                                value={{
                                                    panel,
                                                    panelId,
                                                    filter: panel.filter,
                                                    hide: false,
                                                    panelContainerRef,
                                                    scrollContainerRef,
                                                }}
                                            >
                                                {ghost}
                                            </PanelContextProvider>
                                        )
                                    })()}
                                </div>
                            ) : null}
                        </DragOverlay>,
                        document.body,
                    )}
            </DndContext>
        </AnswersDndRegistryContext.Provider>
    )
}
